import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'components/Luxkit/TextBlocks/HeadingTextBlock'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const CHANGE_SLIDE_MS = 2500
const TRANSITION_ANIMATION_MS = 500

const CardWrapper = styled(Group)`
  width: 100%;
  overflow: hidden;
`

const CardsContainer = styled(Group)`
  width: 100%;
  position: relative;
  top: 0;
  left: 0;
  transition: ease left ${TRANSITION_ANIMATION_MS}ms;
`

const CardContainer = styled(Group)`
  min-width: 100%;
  width: 100%;
  padding: ${rem(80)} 0;
`

interface LoadingSlide {
  title: React.ReactNode | string
  description: string
  image: string
}

type LoadingVariant = 'flights' | 'hotels'

const VARIANTS: Record<LoadingVariant, Array<LoadingSlide>> = {
  flights: [
    {
      image: '2h5x3bxf489ge3vbhowhv',
      title: <>Finding the <i>best</i> prices & airlines</>,
      description: 'Discover unbeatable fares and top-rated airlines.',
    },
    {
      image: '86ukrd82g6wr0qvj1si',
      title: 'Our team are available 24/7',
      description: "We're here for you, anytime, anywhere.",
    },
    {
      image: '783xjama1tn1w5kqcc',
      title: <>Book <i>all</i> your travel in one place</>,
      description: 'One-stop solution for flights, hotels, and more.',
    },
  ],
  hotels: [
    {
      image: 'kfeeuohiqsojdk1jpx5',
      title: <>Finding the <i>best</i> prices & stays</>,
      description: 'Discover unbeatable experiences and top-rated hotels.',
    },
    {
      image: '86ukrd82g6wr0qvj1si',
      title: 'Our team are available 24/7',
      description: "We're here for you, anytime, anywhere.",
    },
    {
      image: '783xjama1tn1w5kqcc',
      title: <>Book <i>all</i> your travel in one place</>,
      description: 'One-stop solution for flights, hotels, and more.',
    },
  ],
}

interface Props {
  variant?: LoadingVariant
}

export default function LoadingAnimationBox({ variant = 'flights' }: Props) {
  const displaySlides = VARIANTS[variant]
  const allSlides = [...displaySlides, displaySlides[0]]

  const [activeIndex, setActiveIndex] = useState(0)
  const cardsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => {
        return prevIndex === allSlides.length - 1 ? 0 : prevIndex + 1
      })
    }, CHANGE_SLIDE_MS)

    return () => clearInterval(interval)
  }, [allSlides.length])

  useEffect(() => {
    const timerId = setTimeout(() => {
      if (!cardsRef.current) return

      if (activeIndex === allSlides.length - 1) {
        cardsRef.current.style.transition = 'none'
      } else {
        cardsRef.current.style.transition = `ease left ${TRANSITION_ANIMATION_MS}ms`
      }
    }, TRANSITION_ANIMATION_MS)

    return () => {
      clearTimeout(timerId)
    }
  }, [activeIndex, allSlides.length])

  return (
    <CardWrapper direction="horizontal">
      <CardsContainer
        ref={cardsRef}
        direction="horizontal"
        style={{ left: `${activeIndex * -100}%` }}
      >
        {allSlides.map((slide, index) => (
          <CardContainer
            key={index}
            direction="vertical"
            verticalAlign="center"
            horizontalAlign="center"
            gap="24 0"
          >
            <Image width="120px" height="120px" id={slide.image} enhancedFormats={false} />

            <VerticalSpacer gap={12}>
              <HeadingTextBlock variant="heading3" align="center">
                {slide.title}
              </HeadingTextBlock>

              <BodyTextBlock variant="medium" align="center">
                {slide.description}
              </BodyTextBlock>
            </VerticalSpacer>
          </CardContainer>
        ))}
      </CardsContainer>
    </CardWrapper>
  )
}
